// React
import { MouseEventHandler, useMemo } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  ActionIcon,
  Button,
  Group,
  Menu,
  useMantineTheme,
  Tooltip,
} from '@mantine/core';
import {
  IconBookmark,
  IconChevronDown,
  IconDeviceFloppy,
  IconCircleMinus,
  IconRepeat,
  IconLock,
} from '@tabler/icons-react';
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { buttonTextColor } from 'utils/buttonUtils';
import { useActiveForm } from '../ClaimFormProvider';
import { MRT_TableInstance } from 'mantine-react-table';
import { Claim } from 'interfaces';
import * as Sentry from '@sentry/react';
import { useCurrentUserStore } from 'store/UserStore';
import { stripNested } from 'utils/miscUtils';
import { getButtonShakeAnimation } from 'utils/styleUtils';

export const ClaimModalSubmitButton: React.FC<{
  table: MRT_TableInstance<Claim>;
  computedColorScheme: string;
  isLoading: boolean;
  handleOnClick: MouseEventHandler<HTMLButtonElement>;
  showResubmitSection?: boolean;
  handleResubmission?: () => void;
  handleFinishBulkClaim?: (isFinished: boolean) => void;
  handleCreateAsIncomplete?: () => void;
  buttonText?: string | null;
  disabled?: boolean;
  menuDisabled?: boolean;
  disabledReason?: string;
  showValidationFeedback?: boolean;
  validationAnimationActive?: boolean;
}> = ({
  table,
  computedColorScheme,
  isLoading,
  handleOnClick,
  showResubmitSection,
  handleResubmission,
  handleFinishBulkClaim,
  handleCreateAsIncomplete,
  buttonText = null,
  disabled = false,
  menuDisabled = disabled,
  disabledReason = 'Editing is disabled',
  showValidationFeedback = false,
  validationAnimationActive = false,
}) => {
  const theme = useMantineTheme();
  const { translate } = useTranslation();
  const { id: currentUserID } = useCurrentUserStore();
  const { saveEditDraft, saveCreateDraft, saveBulkDraftFromRows } =
    useClaimDraftStore();
  const { form, isBulk, formType } = useActiveForm();
  const validationAnimationName = 'claim-validation-shake';
  const validationStyles = useMemo(
    () =>
      showValidationFeedback
        ? {
            animation: validationAnimationActive
              ? `${validationAnimationName} 180ms ease`
              : undefined,
            backgroundColor: theme.colors.red[6],
            borderColor: theme.colors.red[6],
            color: theme.white,
          }
        : {},
    [
      showValidationFeedback,
      validationAnimationActive,
      validationAnimationName,
      theme.colors.red,
      theme.white,
    ]
  );

  return (
    <Group wrap="nowrap" gap={0}>
      <style>{getButtonShakeAnimation(validationAnimationName)}</style>
      <Tooltip disabled={!disabled} label={disabledReason}>
        <Button
          type="submit"
          variant={showValidationFeedback ? 'filled' : 'outline'}
          color={showValidationFeedback ? 'red' : 'green'}
          c={
            showValidationFeedback
              ? theme.white
              : buttonTextColor(computedColorScheme, isLoading)
          }
          onClick={handleOnClick}
          style={{
            borderTopRightRadius: 2,
            borderBottomRightRadius: 2,
            ...validationStyles,
          }}
          loading={isLoading}
          leftSection={<IconDeviceFloppy />}
          data-testid="edit-claim-submit"
          disabled={disabled}
        >
          {buttonText || translate('buttons.save')}
        </Button>
      </Tooltip>
      <Menu
        transitionProps={{ transition: 'pop' }}
        position="bottom"
        withinPortal
      >
        <Menu.Target>
          <ActionIcon
            aria-label={translate('buttons.moreActions', 'More actions')}
            variant="outline"
            color={showValidationFeedback ? 'red' : 'green'}
            c={showValidationFeedback ? 'red' : 'green'}
            disabled={menuDisabled}
            size={36}
            style={{ borderTopLeftRadius: 2, borderBottomLeftRadius: 2 }}
          >
            {menuDisabled ? (
              <IconLock size={16} stroke={1.5} color="white" />
            ) : (
              <IconChevronDown
                size={16}
                stroke={1.5}
                color={showValidationFeedback ? theme.colors.red[6] : 'green'}
              />
            )}
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={
              <IconBookmark
                size={16}
                stroke={1.5}
                color={theme.colors.blue[5]}
              />
            }
            onClick={() => {
              // Instrumentation: snapshot form type & basic claim counts for debugging draft saves
              if (formType === 'bulk' || formType === 'individual') {
                const claims = (form.getValues().claims || []) as any[];
                Sentry.captureMessage('[BULK_AUDIT]Draft Save Request', {
                  level: 'info',
                  extra: {
                    formType,
                    count: claims.length,
                    claims: claims.map((c) => stripNested(c)),
                    user: currentUserID,
                  },
                });
              }
              switch (formType) {
                case 'create':
                  saveCreateDraft(form.getValues().id, form);
                  break;
                case 'edit':
                  saveEditDraft(form.getValues().id, form);
                  break;
                case 'bulk': // opening shared data or overall bulk context
                case 'individual':
                  // Deep snapshot just in case before passing to store (store also deep clones now)
                  const values = form.getValues();
                  saveBulkDraftFromRows(values.shared, values.claims);
                  break;
              }
              table.setCreatingRow(null);
              table.setEditingRow(null);
            }}
            data-testid="edit-claim-save-draft"
          >
            Save as Draft
          </Menu.Item>
          {formType === 'create' && handleCreateAsIncomplete && (
            <Menu.Item
              leftSection={
                <IconCircleMinus
                  size={16}
                  stroke={1.5}
                  color={theme.colors.blue[5]}
                />
              }
              onClick={handleCreateAsIncomplete}
              data-testid="edit-claim-create-incomplete"
            >
              Create as Incomplete
            </Menu.Item>
          )}

          {isBulk && (
            <Menu.Item
              leftSection={
                <IconBookmark
                  size={16}
                  stroke={1.5}
                  color={theme.colors.blue[5]}
                />
              }
              onClick={() => {
                if (!form.isValid()) {
                  form.validate();
                }
                handleFinishBulkClaim(true);
              }}
            >
              Finish Bulk Claim
            </Menu.Item>
          )}
          {showResubmitSection && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={
                  <IconRepeat
                    size={16}
                    stroke={1.5}
                    color={theme.colors.blue[5]}
                  />
                }
                onClick={handleResubmission}
              >
                Mark Claim For Resubmission
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};
